import React from "react";
import { useAuth, useUser } from "@clerk/clerk-react";
import { formatDisplayName } from "../../utils/displayName";
import { formatRelativePast } from "./formatRelativeTime";
import { resolveDashboardLastHere } from "./dashboardLastVisit";
import "./DashboardPresenceStrip.css";

/**
 * Quiet personal row above the dashboard hero.
 *
 * Last-here uses two stored fields (previous / recent), rotated only when
 * Clerk sessionId changes. First login shows "just now".
 *
 * @param {{
 *   secondary?: {
 *     label: string,
 *     actionLabel?: string,
 *     onAction?: () => void,
 *   } | null
 * }} props
 */
export default function DashboardPresenceStrip({ secondary = null }) {
  const { isLoaded: isUserLoaded, user } = useUser();
  const { isLoaded: isAuthLoaded, sessionId } = useAuth();
  const [lastHereLabel, setLastHereLabel] = React.useState(null);

  React.useEffect(() => {
    if (!isUserLoaded || !isAuthLoaded || !user?.id || !sessionId) {
      setLastHereLabel(null);
      return;
    }
    const resolved = resolveDashboardLastHere(user.id, sessionId);
    if (!resolved) {
      setLastHereLabel(null);
      return;
    }
    if (resolved.labelSource === "just_now") {
      setLastHereLabel("just now");
      return;
    }
    setLastHereLabel(formatRelativePast(resolved.iso));
  }, [isUserLoaded, isAuthLoaded, user?.id, sessionId]);

  if (!isUserLoaded || !user) {
    return (
      <div className="dash-presence" aria-hidden="true">
        <div className="dash-presence__primary">
          <span className="dash-presence__skel dash-presence__skel--name" />
        </div>
      </div>
    );
  }

  const firstName =
    formatDisplayName({
      firstName: user.firstName,
      lastName: "",
      format: "first",
      fallback: "",
    }) || "there";

  const showSecondary = Boolean(secondary?.label);

  return (
    <div className="dash-presence">
      <div className="dash-presence__primary">
        <p className="dash-presence__greeting">
          Hi, <span className="dash-presence__name">{firstName}</span>
          {lastHereLabel ? (
            <span className="dash-presence__meta">
              <span className="dash-presence__dot" aria-hidden="true">
                ·
              </span>
              Last here · {lastHereLabel}
            </span>
          ) : null}
        </p>
      </div>

      {showSecondary ? (
        <div className="dash-presence__secondary">
          {secondary.onAction ? (
            <button
              type="button"
              className="dash-presence__secondary-btn"
              onClick={secondary.onAction}
            >
              <span className="dash-presence__secondary-label">{secondary.label}</span>
              {secondary.actionLabel ? (
                <span className="dash-presence__secondary-action">{secondary.actionLabel}</span>
              ) : null}
            </button>
          ) : (
            <p className="dash-presence__secondary-text">{secondary.label}</p>
          )}
        </div>
      ) : null}
    </div>
  );
}
